import React, { useMemo } from "react";

import cn from "classnames";
import {
  addDays,
  differenceInCalendarDays,
  endOfMonth,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
} from "date-fns/esm";

import { Activity } from "@mapmycustomers/shared/types/entity";

import useCalendarView from "@app/component/view/CalendarView/utils/useCalendarView";
import CalendarViewMode from "@app/enum/CalendarViewMode";
import { getWeekEnd, getWeekStart, isSameWeek } from "@app/util/dates";

import styles from "./CalendarCommon.module.scss";

interface Props {
  onClick?: (day: Date) => void;
  timeframeStart: Date;
}

const ExpandedCalendar: React.FC<Props> = ({ onClick, timeframeStart }) => {
  const {
    viewState: { viewMode },
  } = useCalendarView<Activity>();

  const days = useMemo(() => {
    const rangeStart = getWeekStart(startOfMonth(timeframeStart));
    const rangeEnd = getWeekEnd(endOfMonth(timeframeStart));
    const daysToDisplay = differenceInCalendarDays(rangeEnd, rangeStart) + 1;
    return Array.from({ length: daysToDisplay }, (_, index) => addDays(rangeStart, index));
  }, [timeframeStart]);

  return (
    <div className={styles.month}>
      {days.map((day) => (
        <div
          className={cn(styles.day, {
            [styles.highlighted]:
              (viewMode === CalendarViewMode.WEEK && isSameWeek(day, timeframeStart)) ||
              (viewMode === CalendarViewMode.DAY && isSameDay(day, timeframeStart)),
          })}
          key={day.getTime()}
          onClick={() => onClick?.(day)}
        >
          <div
            className={cn(styles.dayInner, {
              [styles.otherMonth]: !isSameMonth(day, timeframeStart),
              [styles.today]: isToday(day),
            })}
          >
            {day.getDate()}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ExpandedCalendar;
